import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import {
  isOvernightFlight,
  calculateConnectionTime,
  formatConnectionTime,
  getConnectionCategory,
  getCategoryStyle
} from '../../utils/connectionUtils';
import ConnectionParametersInfo from './ConnectionParametersInfo';
import ConnectionLegend from './ConnectionLegend';
import PrintButton from './PrintButton';

/**
 * The CombinedTab component that shows both TDR and Brock data
 * With multi-destination row styling similar to TdrTab (no border between sub-rows).
 */
function CombinedTab({ tdrData, brockData, params, inboundETA }) {
  const [hoveredFlight, setHoveredFlight] = useState(null);

  const normalizeFlightNumber = (flightNum) => {
    if (!flightNum) return '';
    const match = flightNum.match(/WN\s*(\d+)/i);
    if (match && match[1]) {
      return parseInt(match[1], 10).toString();
    }
    return flightNum;
  };

  // Merge TDR + Brock data into a single flight list
  const mergeAndCompareData = () => {
    const tdrOutbound = tdrData.outbound || [];
    const brockBins = brockData.bins || {};
    const flightMap = new Map();

    // 1) Insert TDR flights
    tdrOutbound.forEach(flight => {
      const normalizedFlightNumber = normalizeFlightNumber(flight.flightNumber);
      const isOvernight = isOvernightFlight(flight);
      const connectionTime = calculateConnectionTime(flight.etd, inboundETA, isOvernight);
      const cleanDestination = flight.destination
        ? flight.destination.replace(/HOT!\s*/g, '').trim()
        : '';

      if (flightMap.has(normalizedFlightNumber)) {
        // Already have a flight with same flightNumber => add more destinations
        const existingFlight = flightMap.get(normalizedFlightNumber);
        if (!existingFlight.destinations) {
          existingFlight.destinations = [{
            city: existingFlight.destination,
            bags: existingFlight.tdrBags,
            freight: existingFlight.freight || []
          }];
          existingFlight.isMultiDestination = true;
        }
        existingFlight.destinations.push({
          city: cleanDestination,
          bags: flight.bags || (flight.destinations?.[0]?.bags || 0),
          freight: flight.freight || []
        });
        existingFlight.tdrBags = existingFlight.destinations.reduce((sum, d) => sum + d.bags, 0);
        existingFlight.totalDestinations = existingFlight.destinations.length;
      } else {
        // New flight
        const destinations = flight.destinations
          ? flight.destinations.map(dest => ({
              city: dest.city ? dest.city.replace(/HOT!\s*/g, '').trim() : '',
              bags: dest.bags,
              freight: dest.freight || []
            }))
          : [{
              city: cleanDestination,
              bags: flight.bags,
              freight: flight.freight || []
            }];

        const isMultiDestination = flight.destinations && flight.destinations.length > 1;
        flightMap.set(normalizedFlightNumber, {
          originalFlightNumber: flight.flightNumber,
          flightNumber: normalizedFlightNumber,
          registration: flight.registration,
          gate: flight.gate,
          etd: flight.etd,
          destination: cleanDestination,
          destinations: isMultiDestination ? destinations : undefined,
          tdrBags: flight.bags,
          brockBags: 0,
          binDistribution: {},
          discrepancy: false,
          isOvernight,
          isMultiDestination,
          totalDestinations: isMultiDestination ? destinations.length : 1,
          connectionTime,
          freight: flight.freight || []
        });
      }
    });

    // 2) Insert Brock flights
    Object.entries(brockBins).forEach(([binName, binData]) => {
      (binData.flights || []).forEach(flight => {
        const normalizedFlightNumber = normalizeFlightNumber(flight.flightNumber);
        const cleanDestination = flight.dest ? flight.dest.replace(/HOT!\s*/g, '').trim() : '';
        const isOvernight = isOvernightFlight(flight);
        
        // Freight from Brock
        let freightCount = flight.freight;

        if (flightMap.has(normalizedFlightNumber)) {
          const existingFlight = flightMap.get(normalizedFlightNumber);

          // If multi-destination, match up the correct city
          if (existingFlight.destinations) {
            const destMatch = existingFlight.destinations.find(d => {
              const tdrDest = d.city ? d.city.replace(/HOT!\s*/g, '').trim() : '';
              return tdrDest === cleanDestination;
            });
            if (destMatch) {
              if (!existingFlight.destinationBins) {
                existingFlight.destinationBins = new Map();
              }
              if (!existingFlight.destinationBins.has(cleanDestination)) {
                existingFlight.destinationBins.set(cleanDestination, {});
              }
              existingFlight.destinationBins
                .get(cleanDestination)[binName] = flight.bags;

              // Merge freight items
              if (flight.freightItems && flight.freightItems.length > 0) {
                if (!destMatch.brockFreight) {
                  destMatch.brockFreight = [];
                }
                destMatch.brockFreight.push(...flight.freightItems);
              }
            }
          }

          existingFlight.binDistribution[binName] = 
            (existingFlight.binDistribution[binName] || 0) + flight.bags;
          existingFlight.brockBags += flight.bags;
          existingFlight.isOvernight = existingFlight.isOvernight || isOvernight;
          existingFlight.connectionTime = calculateConnectionTime(
            existingFlight.etd, 
            inboundETA, 
            existingFlight.isOvernight
          );

          // Handle freight
          if (flight.freightItems && flight.freightItems.length > 0) {
            if (!existingFlight.brockFreight) {
              existingFlight.brockFreight = [];
            }
            existingFlight.brockFreight.push(...flight.freightItems);
          }

          // Track freight by bin
          if (!existingFlight.freightByBin) {
            existingFlight.freightByBin = {};
          }
          if (freightCount > 0) {
            existingFlight.freightByBin[binName] = 
              (existingFlight.freightByBin[binName] || 0) + freightCount;
          }

          // Discrepancy check
          if (Math.abs(existingFlight.tdrBags - existingFlight.brockBags) > 0.001) {
            existingFlight.discrepancy = true;
          } else {
            existingFlight.discrepancy = false;
          }
        } else {
          // Flight only in Brock
          const freightItems = flight.freightItems || [];
          
          // Initialize freight by bin
          const freightByBin = {};
          if (freightCount > 0) {
            freightByBin[binName] = freightCount;
          }
          
          flightMap.set(normalizedFlightNumber, {
            originalFlightNumber: flight.flightNumber,
            flightNumber: normalizedFlightNumber,
            registration: "-",
            gate: flight.gate,
            etd: flight.est,
            destination: cleanDestination,
            tdrBags: 0,
            brockBags: flight.bags,
            binDistribution: { [binName]: flight.bags },
            freightByBin,
            discrepancy: true,
            onlyInBrock: true,
            isOvernight,
            isTransfer: flight.isTransfer,
            connectionTime: calculateConnectionTime(flight.est, inboundETA, isOvernight),
            freight: [],
            brockFreight: freightItems
          });
        }
      });
    });

    // 3) Convert to array and finalize
    const mergedFlights = Array.from(flightMap.values()).map(f => {
      f.connectionCategory = getConnectionCategory(f.connectionTime, params, f.isOvernight);
      f.allFreight = [...(f.freight || []), ...(f.brockFreight || [])];

      // If multi-destination, mark the first row
      if (f.isMultiDestination && f.destinations) {
        f.isFirstDestination = true;
        f.destinationIndex = 0;
        f.totalDestinations = f.destinations.length;
      }
      return f;
    });

    // 4) Expand multi-dest flights
    const processedFlights = [];
    mergedFlights.forEach(f => {
      if (f.isMultiDestination && f.destinations) {
        // Push the first row
        processedFlights.push(f);

        // Then push subsequent destination rows
        for (let i = 1; i < f.destinations.length; i++) {
          processedFlights.push({
            flightNumber: f.flightNumber,
            originalFlightNumber: f.originalFlightNumber,
            isMultiDestination: true,
            isFirstDestination: false,

            // track index for border styling
            destinationIndex: i,
            totalDestinations: f.destinations.length,

            // carry over
            destination: f.destinations[i].city,
            tdrBags: f.destinations[i].bags,
            brockBagsByDest:
              f.destinationBins?.get(f.destinations[i].city) || {},
            freight: f.destinations[i].freight || [],
            brockFreight: f.destinations[i].brockFreight || [],
            connectionTime: f.connectionTime,
            connectionCategory: f.connectionCategory,
            isOvernight: f.isOvernight,
            discrepancy: f.discrepancy,
            isTransfer: f.isTransfer,
            freightByBin: f.freightByBin,
            gate: f.gate,
            etd: f.etd || f.est
          });
        }
      } else {
        processedFlights.push(f);
      }
    });

    // 5) Sort flights (hot/priority first, then normal, then cold)
    const hotAndPriority = processedFlights.filter(f =>
      (f.connectionCategory === 'hot' || f.connectionCategory === 'priority') &&
      (!f.isMultiDestination || f.isFirstDestination)
    );
    const normal = processedFlights.filter(f =>
      f.connectionCategory === 'normal' &&
      (!f.isMultiDestination || f.isFirstDestination)
    );
    const cold = processedFlights.filter(f =>
      f.connectionCategory === 'cold' &&
      (!f.isMultiDestination || f.isFirstDestination)
    );

    hotAndPriority.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));
    normal.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));
    cold.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));

    const sortedMain = [...hotAndPriority, ...normal, ...cold];
    const result = [];
    sortedMain.forEach(f => {
      result.push(f);
      if (f.isMultiDestination && f.isFirstDestination) {
        // add the continuation rows in the right place
        const continuations = processedFlights.filter(x =>
          x.flightNumber === f.flightNumber && !x.isFirstDestination
        );
        result.push(...continuations);
      }
    });

    return result;
  };

  // Helper to filter flights that have freight
  const getFlightsWithFreight = (flights) => {
    return flights.filter(flight => {
      if (flight.freightByBin && Object.values(flight.freightByBin).some(count => count > 0)) {
        return true;
      }
      if (flight.brockFreight && flight.brockFreight.length > 0) {
        return true;
      }
      return false;
    });
  };

  const getAllBinNames = () => Object.keys(brockData.bins || {}).sort().reverse();

  const calculateTerminatingBags = () => {
    let total = 0;
    Object.values(brockData.bins || {}).forEach(bin => {
      if (bin.terminating && bin.terminating.totalBags) {
        total += bin.terminating.totalBags;
      }
    });
    return total;
  };

  const mergedFlights = mergeAndCompareData();
  const flightsWithFreight = getFlightsWithFreight(mergedFlights);
  const binNames = getAllBinNames();
  const totalTerminatingBags = calculateTerminatingBags();
  const bins = brockData.bins || {};

  // Discrepancy legend
  const discrepancyLegend = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} />
  );

  // Hover highlight color
  const getHoverColor = (category) => {
    if (category === 'hot')      return 'rgba(255, 0, 0, 0.6)';
    if (category === 'priority') return 'rgba(255, 165, 0, 0.5)';
    if (category === 'cold')     return 'rgba(0, 0, 255, 0.35)';
    return 'rgba(255, 255, 255, 0.1)';
  };

  // Base color
  const getBaseColor = (category) => {
    if (category === 'hot')      return 'rgba(255, 0, 0, 0.4)';
    if (category === 'priority') return 'rgba(255, 165, 0, 0.3)';
    if (category === 'cold')     return 'rgba(0, 0, 255, 0.2)';
    return 'transparent';
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <h3 style={{ color: '#ccc' }}>Combined View</h3>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <PrintButton 
          tdrData={tdrData}
          brockData={brockData}
          params={params}
          inboundETA={inboundETA}
        />
      </Box>

      <ConnectionParametersInfo 
        params={params} 
        color="#ccc" 
        extraInfo="Discrepancies between TDR and Brock bag counts highlighted in orange"
      />
      <ConnectionLegend 
        params={params} 
        extraLegends={[discrepancyLegend]}
      />

      {/* Main Bags Table */}
      <TableContainer component={Paper} sx={{ marginBottom: '2rem', backgroundColor: '#1f2937' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Flight #</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Registration</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Gate</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>ETD</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Connection</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Destination</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>TDR Bags</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Brock Bags</TableCell>
              {binNames.map(binName => (
                <TableCell key={binName} sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Bin {binName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedFlights.map((flight, idx) => {
              const isHovered = hoveredFlight === flight.flightNumber;
              const hoverColor = getHoverColor(flight.connectionCategory);
              const baseStyle = getCategoryStyle(flight.connectionCategory) || {};

              // Single-dest vs multi-dest
              const isMultiDest = !!flight.isMultiDestination;
              const destinationIndex = flight.destinationIndex ?? 0;
              const lastIndex = (flight.totalDestinations || 1) - 1;

              // We'll mimic TdrTab's approach:
              // SINGLE-DEST => top border in the cells
              // MULTI-DEST first & middle rows => no bottom border
              // MULTI-DEST last row => normal MUI bottom border
              
              let rowSx = {
                ...baseStyle,
                backgroundColor: isHovered ? hoverColor : baseStyle.backgroundColor,
                cursor: 'default',
              };

              // For single-destination flights, we add a top border in each cell:
              let cellStyles = { color: '#fff' };
              if (!isMultiDest) {
                cellStyles = {
                  color: '#fff',
                  borderTop: '1px solid rgba(255, 255, 255, 0.87)'
                };
              }

              // For multi-destination:
              if (isMultiDest) {
                // If it's NOT the last row => remove bottom border
                if (destinationIndex < lastIndex) {
                  rowSx = {
                    ...rowSx,
                    '& td': {
                      borderBottom: 'none',
                    },
                  };
                }
                // (the last row in a multi-dest block gets the normal bottom border from MUI)
              }

              return (
                <TableRow
                  key={`${flight.flightNumber}-${idx}`}
                  sx={rowSx}
                  onMouseEnter={() => setHoveredFlight(flight.flightNumber)}
                  onMouseLeave={() => setHoveredFlight(null)}
                >
                  {/* If it's the first row of a multi-dest flight or single-dest, we do rowSpan */}
                  {(!flight.isMultiDestination || flight.isFirstDestination) ? (
                    <TableCell 
                      rowSpan={flight.totalDestinations || 1}
                      sx={{
                        ...cellStyles,
                        fontWeight: flight.isOvernight ? 'bold' : 'normal'
                      }}
                    >
                      {normalizeFlightNumber(flight.originalFlightNumber || flight.flightNumber)}
                      {flight.isTransfer && (
                        <Typography 
                          component="span" 
                          sx={{ 
                            ml: 1, 
                            color: '#3498db', 
                            fontWeight: 'bold',
                            fontSize: '0.8rem' 
                          }}
                        >
                          (Transfer)
                        </Typography>
                      )}
                      {flight.isOvernight && (
                        <Typography 
                          component="span" 
                          sx={{ ml: 1, color: '#F9B612', fontWeight: 'bold', fontSize: '0.8rem' }}
                        >
                          (Overnight)
                        </Typography>
                      )}
                    </TableCell>
                  ) : null}

                  {(!flight.isMultiDestination || flight.isFirstDestination) ? (
                    <TableCell 
                      rowSpan={flight.totalDestinations || 1}
                      sx={cellStyles}
                    >
                      {flight.registration}
                    </TableCell>
                  ) : null}

                  {(!flight.isMultiDestination || flight.isFirstDestination) ? (
                    <TableCell 
                      rowSpan={flight.totalDestinations || 1}
                      sx={cellStyles}
                    >
                      {flight.gate}
                    </TableCell>
                  ) : null}

                  {(!flight.isMultiDestination || flight.isFirstDestination) ? (
                    <TableCell 
                      rowSpan={flight.totalDestinations || 1}
                      sx={cellStyles}
                    >
                      {flight.etd}
                    </TableCell>
                  ) : null}

                  {(!flight.isMultiDestination || flight.isFirstDestination) ? (
                    <TableCell 
                      rowSpan={flight.totalDestinations || 1}
                      sx={{
                        ...cellStyles,
                        color: flight.connectionTime < 0
                          ? (params.isPriorityFlight 
                              ? 'rgba(249, 182, 18, 0.9)'
                              : 'rgba(255, 0, 0, 0.9)')
                          : '#fff',
                        fontWeight: (flight.connectionTime < 0 || flight.isOvernight) ? 'bold' : 'normal'
                      }}
                    >
                      {formatConnectionTime(flight.connectionTime)}
                      {flight.isOvernight && (
                        <Typography 
                          component="span" 
                          sx={{ display: 'block', color: '#F9B612', fontWeight: 'bold', fontSize: '0.75rem' }}
                        >
                          Next Day
                        </Typography>
                      )}
                    </TableCell>
                  ) : null}

                  {/* Destination cell */}
                  <TableCell sx={cellStyles}>
                    {isMultiDest && flight.isFirstDestination && flight.destinations
                      ? flight.destinations[0].city
                      : (isMultiDest && !flight.isFirstDestination
                          ? flight.destination
                          : flight.destination
                        )
                    }
                  </TableCell>

                  {/* TDR Bags */}
                  <TableCell 
                    sx={{
                      ...cellStyles,
                      color: flight.discrepancy ? 'rgba(255, 165, 0, 1)' : '#fff',
                      fontWeight: flight.discrepancy ? 'bold' : 'normal'
                    }}
                  >
                    {isMultiDest && flight.isFirstDestination && flight.destinations
                      ? flight.destinations[0].bags
                      : flight.tdrBags
                    }
                  </TableCell>

                  {/* Brock Bags */}
                  <TableCell 
                    sx={{
                      ...cellStyles,
                      color: flight.discrepancy ? 'rgba(255, 165, 0, 1)' : '#fff',
                      fontWeight: flight.discrepancy ? 'bold' : 'normal'
                    }}
                  >
                    {isMultiDest && !flight.isFirstDestination
                      ? (flight.brockBagsByDest
                          ? Object.values(flight.brockBagsByDest).reduce((sum, val) => sum + val, 0)
                          : 0
                        )
                      : flight.brockBags
                    }
                  </TableCell>

                  {/* Bin columns */}
                  {binNames.map(binName => (
                    <TableCell key={binName} sx={cellStyles}>
                      {isMultiDest && !flight.isFirstDestination
                        ? (flight.brockBagsByDest && flight.brockBagsByDest[binName]
                            ? flight.brockBagsByDest[binName]
                            : 0
                          )
                        : (flight.binDistribution[binName] || 0)
                      }
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}

            {/* Terminating Bags row */}
            <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} colSpan={7}>
                Terminating Bags
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                {totalTerminatingBags}
              </TableCell>
              {binNames.map(binName => (
                <TableCell key={binName} sx={{ color: '#fff' }}>
                  {bins[binName]?.terminating?.totalBags || 0}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Freight Table */}
      {flightsWithFreight.length > 0 && (
        <>
          <Divider sx={{ marginBottom: '2rem', bgcolor: 'rgba(255,255,255,0.1)' }} />
          
          <Typography variant="h5" sx={{ color: '#F9B612', marginBottom: '1rem' }}>
            Freight Summary
          </Typography>
          
          <TableContainer component={Paper} sx={{ marginBottom: '2rem', backgroundColor: '#1f2937' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Flight #</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Registration</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Gate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>ETD</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Connection</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Destination</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Type</TableCell>
                  {binNames.sort((a, b) => b.localeCompare(a)).map(binName => (
                    <TableCell key={binName} sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>
                      Bin {binName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {flightsWithFreight.map((flight, idx) => {
                  const baseStyle = {
                    backgroundColor: getBaseColor(flight.connectionCategory)
                  };
                  const isHovered = hoveredFlight === flight.flightNumber;
                  const hoverColor = getHoverColor(flight.connectionCategory);
                  const rowStyle = {
                    ...baseStyle,
                    backgroundColor: isHovered ? hoverColor : baseStyle.backgroundColor,
                    cursor: 'default'
                  };

                  // Calculate total freight across bins
                  let totalFreight = 0;
                  if (flight.freightByBin) {
                    totalFreight = Object.values(flight.freightByBin).reduce(
                      (sum, count) => sum + count,
                      0
                    );
                  }
                  
                  // If there's truly no freight, skip row
                  if (totalFreight === 0) return null;

                  // Decide which type label to show:
                  let freightType = "";
                  // Check TDR freight first
                  if (flight.freight && flight.freight.length > 0) {
                    freightType = flight.freight[0].type || "";
                  } else if (flight.brockFreight && flight.brockFreight.length > 0) {
                    freightType = flight.brockFreight[0].type || "";
                  }

                  return (
                    <TableRow
                      key={`freight-${flight.flightNumber}-${idx}`}
                      sx={rowStyle}
                      onMouseEnter={() => setHoveredFlight(flight.flightNumber)}
                      onMouseLeave={() => setHoveredFlight(null)}
                    >
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.flightNumber}
                        {flight.isTransfer && (
                          <Typography 
                            component="span" 
                            sx={{ 
                              ml: 1, 
                              color: '#3498db', 
                              fontWeight: 'bold',
                              fontSize: '0.8rem'
                            }}
                          >
                            (Transfer)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.registration}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.gate}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.etd || flight.est}
                      </TableCell>
                      <TableCell sx={{
                        color: flight.connectionTime < 0
                          ? (params.isPriorityFlight
                              ? 'rgba(249, 182, 18, 0.9)'
                              : 'rgba(255, 0, 0, 0.9)')
                          : '#fff',
                        fontWeight: (flight.connectionTime < 0 || flight.isOvernight) ? 'bold' : 'normal'
                      }}>
                        {formatConnectionTime(flight.connectionTime)}
                        {flight.isOvernight && (
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              color: '#F9B612',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          >
                            Next Day
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.destination}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {freightType ? (
                          <Chip
                            label={freightType}
                            size="small"
                            sx={{
                              backgroundColor: '#2c3e50',
                              color: '#fff',
                              fontWeight: 'bold'
                            }}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                      {binNames
                        .sort((a, b) => b.localeCompare(a))
                        .map(binName => {
                          let binFreight = 0;
                          if (flight.freightByBin && flight.freightByBin[binName]) {
                            binFreight = flight.freightByBin[binName];
                          }
                          return (
                            <TableCell
                              key={binName}
                              sx={{ color: '#fff', textAlign: 'center' }}
                            >
                              {binFreight > 0 ? binFreight : '-'}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
}

export default CombinedTab;